import React, { useState, useEffect } from 'react';
import { motion, useScroll, useTransform } from 'motion/react';
import { 
  Zap, MapPin, Users, TrendingUp, Sparkles, ArrowRight, 
  Phone, Fuel, ShoppingBag, Shield, MessageSquare, 
  Radio, DollarSign, Eye, ChevronDown, Play, Star,
  CheckCircle, Globe, BarChart3, Mic, Clock
} from 'lucide-react';
import { useNavigate } from 'react-router';

export default function FrontAdvert() {
  const navigate = useNavigate();
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const { scrollYProgress } = useScroll();
  const backgroundY = useTransform(scrollYProgress, [0, 1], ['0%', '100%']);
  const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);

  const features = [
    {
      icon: Zap,
      title: 'Light Watch',
      description: 'Track power outages in real-time across your neighborhood',
      gradient: 'from-yellow-500 to-orange-600',
      emoji: '⚡'
    },
    {
      icon: Users,
      title: 'Digital Ajo Circles',
      description: 'Join trusted savings groups and build financial security',
      gradient: 'from-green-500 to-emerald-600',
      emoji: '💰'
    },
    {
      icon: ShoppingBag,
      title: 'Market Basket Tracker',
      description: 'Compare prices across markets and find the best deals',
      gradient: 'from-purple-500 to-pink-600',
      emoji: '🛒'
    },
    {
      icon: Mic,
      title: 'Voice-Note Reporting',
      description: 'Report incidents hands-free with voice messages',
      gradient: 'from-blue-500 to-cyan-600',
      emoji: '🎤'
    },
    {
      icon: Fuel,
      title: 'Fuel Watch Hub',
      description: 'Find available fuel stations and current prices',
      gradient: 'from-red-500 to-orange-600',
      emoji: '⛽'
    },
    {
      icon: Radio,
      title: 'Neighborhood Swap & Shop',
      description: 'Trade items safely with verified neighbors',
      gradient: 'from-indigo-500 to-purple-600',
      emoji: '🔄'
    },
    {
      icon: Shield,
      title: 'SafePay Escrow',
      description: 'Secure transactions with built-in protection',
      gradient: 'from-emerald-500 to-green-600',
      emoji: '🛡️'
    },
    {
      icon: Eye,
      title: 'Bus Stop Crowd Watch',
      description: 'Check real-time crowd levels before you travel',
      gradient: 'from-cyan-500 to-blue-600',
      emoji: '👀'
    }
  ];

  const stats = [
    { value: '10,045+', label: 'Active Users', icon: Users },
    { value: '37', label: 'States Covered', icon: MapPin },
    { value: '50+', label: 'Reports/Min', icon: TrendingUp },
    { value: '99.9%', label: 'Uptime', icon: CheckCircle }
  ];

  const testimonials = [
    {
      name: 'Chiamaka O.',
      location: 'Lekki, Lagos',
      text: 'Sentlo saved me hours! I checked fuel availability before leaving home. Game changer!',
      avatar: '👩🏾',
      rating: 5
    },
    {
      name: 'Tunde A.',
      location: 'Ikeja, Lagos',
      text: 'The Light Watch feature is incredible. I can plan my day around power schedules now.',
      avatar: '👨🏿',
      rating: 5
    },
    {
      name: 'Blessing M.',
      location: 'Abuja Central',
      text: 'Digital Ajo changed my life. I\'ve saved ₦200,000 in 3 months with my circle!',
      avatar: '👩🏾‍🦱',
      rating: 5
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-green-900 to-emerald-900 text-white overflow-hidden">
      {/* Animated Background Particles */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        {[...Array(50)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-white rounded-full"
            initial={{ 
              x: Math.random() * window.innerWidth, 
              y: Math.random() * window.innerHeight,
              opacity: Math.random() * 0.5
            }}
            animate={{ 
              y: [null, Math.random() * window.innerHeight],
              opacity: [null, 0, Math.random() * 0.5]
            }}
            transition={{ 
              duration: Math.random() * 10 + 10, 
              repeat: Infinity,
              ease: 'linear'
            }}
          />
        ))}
      </div>

      {/* Hero Section */}
      <motion.section 
        style={{ opacity }}
        className="relative min-h-screen flex items-center justify-center px-4 overflow-hidden"
      >
        {/* Gradient Orbs */}
        <div className="absolute top-20 left-10 w-72 h-72 bg-green-500/30 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-emerald-500/20 rounded-full blur-3xl animate-pulse delay-1000" />
        
        <div className="relative z-10 max-w-6xl mx-auto text-center">
          {/* Logo Animation */}
          <motion.div
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ duration: 0.8, type: 'spring' }}
            className="mb-8"
          >
            <div className="inline-flex items-center gap-3 px-6 py-3 bg-white/10 backdrop-blur-xl rounded-full border border-white/20">
              <Sparkles className="w-6 h-6 text-yellow-400" />
              <span className="text-2xl font-black">SENTLO</span>
              <span className="px-3 py-1 bg-green-500 rounded-full text-xs font-bold">NIGERIA 🇳🇬</span>
            </div>
          </motion.div>

          {/* Main Headline */}
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="text-5xl md:text-7xl font-black mb-6 leading-tight"
          >
            Your City's{' '}
            <span className="bg-gradient-to-r from-green-400 via-emerald-400 to-cyan-400 bg-clip-text text-transparent">
              Intelligence Network
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="text-xl md:text-2xl text-gray-300 mb-8 max-w-3xl mx-auto leading-relaxed"
          >
            Turn every Nigerian into a neighborhood reporter. Real-time intel on traffic, power, fuel, floods, and daily urban challenges.
          </motion.p>

          {/* CTA Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
            className="flex flex-col sm:flex-row gap-4 justify-center mb-12"
          >
            <button
              onClick={() => navigate('/app')}
              className="group px-8 py-4 bg-gradient-to-r from-green-500 to-emerald-600 rounded-2xl font-black text-lg shadow-2xl shadow-green-500/50 hover:shadow-green-500/80 hover:scale-105 transition-all flex items-center gap-3 justify-center"
            >
              <span>Launch App</span>
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>
            
            <button
              onClick={() => setIsVideoPlaying(true)}
              className="px-8 py-4 bg-white/10 backdrop-blur-xl rounded-2xl font-bold text-lg border-2 border-white/20 hover:bg-white/20 hover:scale-105 transition-all flex items-center gap-3 justify-center"
            >
              <Play className="w-5 h-5" />
              <span>Watch Demo</span>
            </button>
          </motion.div>

          {/* Stats Bar */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9 }}
            className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto"
          >
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 1 + index * 0.1 }}
                className="bg-white/10 backdrop-blur-xl rounded-2xl p-6 border border-white/20"
              >
                <stat.icon className="w-8 h-8 mb-3 mx-auto text-green-400" />
                <div className="text-3xl font-black mb-1">{stat.value}</div>
                <div className="text-sm text-gray-400">{stat.label}</div>
              </motion.div>
            ))}
          </motion.div>

          {/* Scroll Indicator */}
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="absolute bottom-10 left-1/2 -translate-x-1/2"
          >
            <ChevronDown className="w-8 h-8 text-white/50" />
          </motion.div>
        </div>
      </motion.section>

      {/* Features Grid */}
      <section className="relative py-24 px-4">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-6xl font-black mb-4">
              8 Powerful Features
            </h2>
            <p className="text-xl text-gray-400">
              Everything you need to navigate Nigeria's cities with confidence
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ scale: 1.05, y: -5 }}
                className="group relative bg-white/5 backdrop-blur-xl rounded-3xl p-6 border border-white/10 hover:border-white/30 transition-all cursor-pointer overflow-hidden"
              >
                {/* Gradient Background on Hover */}
                <div className={`absolute inset-0 bg-gradient-to-br ${feature.gradient} opacity-0 group-hover:opacity-10 transition-opacity`} />
                
                <div className="relative z-10">
                  <div className="text-6xl mb-4">{feature.emoji}</div>
                  <h3 className="text-xl font-black mb-2">{feature.title}</h3>
                  <p className="text-sm text-gray-400 leading-relaxed">
                    {feature.description}
                  </p>
                </div>

                {/* Shine Effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="relative py-24 px-4 bg-black/20">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-6xl font-black mb-4">
              How Sentlo Works
            </h2>
            <p className="text-xl text-gray-400">
              Three simple steps to stay informed
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { 
                step: '01', 
                title: 'Report', 
                description: 'Share what you see: traffic, power cuts, fuel scarcity, or events',
                icon: MessageSquare,
                color: 'text-green-400'
              },
              { 
                step: '02', 
                title: 'Verify', 
                description: 'Community verifies reports for accuracy and reliability',
                icon: CheckCircle,
                color: 'text-blue-400'
              },
              { 
                step: '03', 
                title: 'Navigate', 
                description: 'Make informed decisions with real-time, verified information',
                icon: MapPin,
                color: 'text-purple-400'
              }
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.2 }}
                className="relative"
              >
                <div className="bg-white/5 backdrop-blur-xl rounded-3xl p-8 border border-white/10">
                  <div className={`text-7xl font-black ${item.color} mb-4 opacity-20`}>
                    {item.step}
                  </div>
                  <item.icon className={`w-12 h-12 mb-4 ${item.color}`} />
                  <h3 className="text-2xl font-black mb-3">{item.title}</h3>
                  <p className="text-gray-400 leading-relaxed">
                    {item.description}
                  </p>
                </div>
                
                {/* Connector Line */}
                {index < 2 && (
                  <div className="hidden md:block absolute top-1/2 -right-4 w-8 h-0.5 bg-gradient-to-r from-white/30 to-transparent" />
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="relative py-24 px-4">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-6xl font-black mb-4">
              Loved by Nigerians
            </h2>
            <p className="text-xl text-gray-400">
              Real stories from real users across Nigeria
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white/5 backdrop-blur-xl rounded-3xl p-6 border border-white/10"
              >
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <p className="text-gray-300 mb-6 leading-relaxed">
                  "{testimonial.text}"
                </p>
                <div className="flex items-center gap-3">
                  <div className="text-4xl">{testimonial.avatar}</div>
                  <div>
                    <div className="font-bold">{testimonial.name}</div>
                    <div className="text-sm text-gray-500 flex items-center gap-1">
                      <MapPin className="w-3 h-3" />
                      {testimonial.location}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative py-32 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-3xl p-12 shadow-2xl shadow-green-500/50"
          >
            <h2 className="text-4xl md:text-6xl font-black mb-6">
              Ready to Navigate Nigeria Smarter?
            </h2>
            <p className="text-xl mb-8 opacity-90">
              Join 10,045+ Nigerians making informed decisions every day
            </p>
            <button
              onClick={() => navigate('/app')}
              className="group px-10 py-5 bg-white text-green-600 rounded-2xl font-black text-xl shadow-xl hover:shadow-2xl hover:scale-105 transition-all flex items-center gap-3 mx-auto"
            >
              <span>Get Started Free</span>
              <ArrowRight className="w-6 h-6 group-hover:translate-x-2 transition-transform" />
            </button>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/10 py-12 px-4">
        <div className="max-w-6xl mx-auto text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Sparkles className="w-6 h-6 text-green-400" />
            <span className="text-2xl font-black">SENTLO</span>
          </div>
          <p className="text-gray-500 mb-6">
            A hyperlocal intelligence network for Nigeria's cities
          </p>
          <div className="flex gap-6 justify-center text-sm text-gray-500">
            <button onClick={() => navigate('/landing')} className="hover:text-white transition-colors">
              Full Landing
            </button>
            <button onClick={() => navigate('/app')} className="hover:text-white transition-colors">
              Launch App
            </button>
            <button onClick={() => navigate('/promo')} className="hover:text-white transition-colors">
              Promo
            </button>
          </div>
        </div>
      </footer>

      {/* Video Modal */}
      {isVideoPlaying && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 bg-black/90 backdrop-blur-xl z-50 flex items-center justify-center p-4"
          onClick={() => setIsVideoPlaying(false)}
        >
          <motion.div
            initial={{ scale: 0.8 }}
            animate={{ scale: 1 }}
            className="relative max-w-4xl w-full aspect-video bg-gray-900 rounded-3xl overflow-hidden border-2 border-green-500/50 shadow-2xl shadow-green-500/20"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Demo Video Placeholder - Blank for now */}
            <div className="w-full h-full flex items-center justify-center">
              <p className="text-gray-500 text-lg">Demo video coming soon...</p>
            </div>
            
            <button
              onClick={() => setIsVideoPlaying(false)}
              className="absolute top-4 right-4 w-12 h-12 bg-black/80 hover:bg-black rounded-full flex items-center justify-center transition-colors backdrop-blur-sm border border-white/20 z-10"
            >
              <span className="text-2xl">×</span>
            </button>
          </motion.div>
        </motion.div>
      )}
    </div>
  );
}
